import { faRotateLeft } from '@fortawesome/free-solid-svg-icons/faRotateLeft';
import { faUserSlash } from '@fortawesome/free-solid-svg-icons/faUserSlash';
import { add } from 'date-fns';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AtiraIcon } from '../../../components/AtiraIcon';
import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Text } from '../../../components/Text';
import { WarningModal } from '../../../components/WarningModal';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { userSliceSelectors } from '../../../redux/user/user.selector';
import { userActions } from '../../../redux/user/user.slice';
import { Breakpoints } from '../../../theme/Breakpoints';
import { Rounded } from '../../../theme/Rounded';
import { Spacing } from '../../../theme/Spacing';
import { AtiraToast } from '../../../utils/AtiraToast';
import { standardDate } from '../../../utils/Date';

const Container = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
  width: 100%;
`;

const SectionTitle = styled(Text)`
  font-weight: bold;
  height: 2rem;
`;

const Section = styled(Flex)`
  background-color: ${({ theme }) => theme.bgCanvas};
  border-radius: ${Rounded.lg};
  padding: ${Spacing.m};
  flex-direction: column;
  gap: ${Spacing.s};
  margin-bottom: ${Spacing.m};
`;

const StyledButton = styled(Button)`
  height: 2.3rem;
  margin-top: ${Spacing.l};
  width: fit-content;

  &:hover {
    background-color: ${({ theme }) => theme.mainHover};
  }
`;

const ResponsiveFlex = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.l};
  align-items: flex-start;

  @media (min-width: ${Breakpoints.MOBILE}) {
    flex-direction: row;
    align-items: center;

    max-width: 60%;
  }
`;

export const SettingsAccountDeletion: React.FC = () => {
  const [warningModalVisible, setWarningModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation(undefined, {
    keyPrefix: 'settings.general',
  });
  const dispatch = useAppDispatch();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const isMarkedForDeletion = Boolean(user?.isMarkedForDeletionAt);

  const onToggleMarkForDeletion = async () => {
    try {
      setLoading(true);

      if (isMarkedForDeletion) {
        await dispatch(
          userActions.undoDeleteUser({ userId: user._id }),
        ).unwrap();
      } else {
        await dispatch(
          userActions.markUserForDeletion({ userId: user._id }),
        ).unwrap();
      }

      await dispatch(
        userActions.getLoggedInUser({ userId: user._id }),
      ).unwrap();

      AtiraToast.success(
        t(
          `${isMarkedForDeletion ? 'undoDeletion' : 'markForDeletion'}.success`,
        ),
      );
      setWarningModalVisible(false);
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <SectionTitle>{t('title')}</SectionTitle>

      <Section>
        <ResponsiveFlex>
          <AtiraIcon
            icon={isMarkedForDeletion ? faRotateLeft : faUserSlash}
            size="3x"
            color={isMarkedForDeletion ? 'warning' : 'danger'}
          />

          <Flex flexDirection="column" gap="s">
            <Text fontWeight={'bold'}>
              {t(
                `${isMarkedForDeletion ? 'undoDeletion' : 'markForDeletion'}.subtitle`,
              )}
            </Text>

            <Text fontSize="s" color="fgSecondary">
              <Trans
                i18nKey={
                  isMarkedForDeletion
                    ? 'settings.general.undoDeletion.description'
                    : 'settings.general.markForDeletion.description'
                }
                values={{
                  date: standardDate(
                    add(user.isMarkedForDeletionAt!, { days: 7 }),
                  ),
                }}
                components={[<strong />]}
              />
            </Text>
          </Flex>
        </ResponsiveFlex>

        <StyledButton
          onClick={() => setWarningModalVisible(true)}
          width="12rem"
        >
          {t(
            `${isMarkedForDeletion ? 'undoDeletion' : 'markForDeletion'}.button`,
          )}
        </StyledButton>
      </Section>

      <WarningModal
        title={t(
          `${isMarkedForDeletion ? 'undoDeletion' : 'markForDeletion'}.modal.title`,
        )}
        description={t(
          `${isMarkedForDeletion ? 'undoDeletion' : 'markForDeletion'}.modal.description`,
        )}
        onConfirm={onToggleMarkForDeletion}
        loading={loading}
        open={warningModalVisible}
        onClose={() => setWarningModalVisible(false)}
      />
    </Container>
  );
};
